import React, { forwardRef, useEffect, useState } from "react";
import { INMATE_DETAILS_MODE } from ".";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import {
  getRefListItemsbyName,
  getRefListNameByID,
  hasCheckSecurity,
  isNonEmptyVariable,
  removeNullFromText,
} from "@/util/common_function";
import { useDispatch, useSelector } from "react-redux";
import useDialog from "@/hooks/useDialog";
import { setBookings } from "@/slices/bookingsSlice";
import HouseAndTracksTable from "../HouseandTracking/HousandTrackingTable";
import HouseAndTracksInfoTables from "../HouseandTracking/HouseandTrackingInfoTable";
import HouseandTracksClearPopup from "../HouseandTracking/HouseAndTrackingClear";
import HouseAndTracksForm from "../HouseandTracking/HouseAndTrackingForm";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  REF_LIST_CONSTANT,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { INMATE_FLAG_TYPE } from "@/util/iconsUtil";
import _ from "lodash";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";

const HouseTrack = forwardRef(
  ({ mode = INMATE_DETAILS_MODE.EDIT, houseFormRef }, ref) => {
    const dispatch = useDispatch();
    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state?.bookings
    );

    // Declarations
    const [showDetailsPopup, setShowDetailsPopup] = useState(false);
    const [showLogInfo, setShowLogInfo] = useState(false);
    const [showClearPopup, setShowClearPopup] = useState(false);
    const [selectedRow, setSelectedRow] = useState();
    const [selectedIndex, setSelectedIndex] = useState();
    const [actionType, setActionType] = useState();
    const [isEdit, setIsEdit] = useState(false);
    const [isScheduled, setScheduled] = useState(false);
    const [isReturn, setIsReturn] = useState(false);
    const [lastLoan, setLastLoan] = useState(null);
    const [lastCell, setLastCell] = useState(null);
    const [isInLoan, setIsInLoan] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const fullSearch = useSelector(state => state.fullSearch);
    dayjs.extend(isSameOrBefore);

    const actionTypes = {
      ADD: "Add",
      EDIT: "Modify",
    };

    const { showDialog } = useDialog();

    const errorText =
      "You can only change a movement that is in Transit to Complete. You cannot changed it back to Pending or Cancel the movement. Once the inmate is Transit, the move is considered to have actually taken place; setting the status to In Transit simply indicates that the move may take a while finish it. If you need to move the inmate somewhere else, complete this move and then add a new movement transaction";

    // Functions
    const handleOnRowClick = (data, index) => {
      setSelectedIndex(index);
      setSelectedRow(bookings.inmateTracking[index]);
    };
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));

    const handleShowPopup = (action) => {
      setActionType(action);
      if (action === actionTypes.ADD) {
        setSelectedRow(null);
      } else {
        setSelectedRow(bookings.inmateTracking[selectedIndex]);
        setIsEdit(true);
      }
      setShowDetailsPopup(true);
    };

    const handleLogClick = () => {
      setShowLogInfo(true);
    };

    const handleClearClick = async () => {
      showDialog("confirm", {
        title: "Warning",
        content:
          "Are you sure you want to clear the inmate's assigned housing?",
        onConfirm: () => {
          setShowClearPopup(true);
        },
        onCancel: () => {
          setShowClearPopup(false);
        },
      });
    };

    const getRestrictForMoveReason = (content) => {
      return new Promise((resolve, reject) => {
        showDialog("confirm", {
          title: "Warning",
          content: content,
          onConfirm: () => {
            resolve(true);
          },
          onCancel: () => {
            resolve(false);
          },
        });
      });
    };

    const handleClearingHousing = (value) => {
      const data = {
        inmateLocation: "CLEARED",
        comments: value,
        actualDate: dayjs()?.format("MM/DD/YYYY"),
        actualTime: dayjs()?.format("HH:mm"),
      };

      dispatch(
        setBookings({
          inmateTracking: [...(bookings?.inmateTracking || []), data],
        })
      );
      setShowClearPopup(false);
    };

    const handleSave = () => {
      houseFormRef?.current
        ?.validateFields()
        .then(async () => {
          const values = houseFormRef?.current?.getFormValues();

          const isTransportAdded =
            (values?.inmateLocation === "Perm Housing Change" ||
              values?.inmateLocation === "Transport") &&
            values?.movementStatus === "Complete";

          if (!bookings?.jacketId && isTransportAdded && !fullSearch) {
            showDialog("info", {
              title: "Warning",
              content: "You must save the Booking record at least once before you can Transport the inmate.",
              confirmButtonText: "Ok",
            });
            return;
          }

          if (
            actionType === actionTypes.EDIT &&
            bookings?.inmateTracking?.[selectedIndex].movementStatus ===
              "In Transit" &&
            !(
              values?.movementStatus === "In Transit" ||
              values?.movementStatus === "Complete"
            )
          ) {
            showDialog("info", {
              title: "Warning",
              content: errorText,
              confirmButtonText: "Ok",
            });
            return;
          }

          if (values?.moveReason === "Receive Inmate") {
            const content = `You should only enter a Reason for Movement of "Recieve Inmate" if you are recieving the inmate as the result of a Transport from another facility Are you recieving the inmate from another facility?`;
            const isMovementReason = await getRestrictForMoveReason(content);

            if (!isMovementReason) {
              houseFormRef?.current?.setFormValue("moveReason", "");
              return;
            }
          }

          const data = {
            ...values,
            expectedBackDate: isNonEmptyVariable(values?.expectedBackDateTime)
              ? dayjs(values?.expectedBackDateTime)?.format("MM/DD/YYYY")
              : null,
            expectedBackTime: isNonEmptyVariable(values?.expectedBackDateTime)
              ? dayjs(values?.expectedBackDateTime)?.format("HH:mm")
              : null,
            returnBackDate: isNonEmptyVariable(values?.returnBackDateTime)
              ? dayjs(values?.returnBackDateTime)?.format("MM/DD/YYYY")
              : null,
            returnBackTime: isNonEmptyVariable(values?.returnBackDateTime)
              ? dayjs(values?.returnBackDateTime)?.format("HH:mm")
              : null,
            scheduledDate: isNonEmptyVariable(values?.scheduleDateTime)
              ? dayjs(values?.scheduleDateTime)?.format("MM/DD/YYYY")
              : null,
            scheduledTime: isNonEmptyVariable(values?.scheduleDateTime)
              ? dayjs(values?.scheduleDateTime)?.format("HH:mm")
              : null,
            actualDate: isNonEmptyVariable(values?.actualDateTime)
              ? dayjs(values?.actualDateTime)?.format("MM/DD/YYYY")
              : null,
            actualTime: isNonEmptyVariable(values?.actualDateTime)
              ? dayjs(values?.actualDateTime)?.format("HH:mm")
              : null,
          };

          if (!data?.enterBusiness) {
            data.businesses = null;
          }

          delete data?.returnBackDateTime;
          delete data?.expectedBackDateTime;
          delete data?.scheduleDateTime;
          delete data?.actualDateTime;

          // const detail = flagDetails[INMATE_FLAG_TYPE.IN_JAIL];

          dispatch(
            setBookings({
              inmateTracking:
                actionType === actionTypes.EDIT
                  ? bookings?.inmateTracking.map((item, i) =>
                      i === selectedIndex ? { ...item, ...data } : item
                    )
                  : [...(bookings?.inmateTracking || []), data],
            })
          );
          setIsLoading(() => true);
          const url =
            API_ENDPOINTS.getLatestBookingsByjacketID + bookings?.jacketId;
          api
            .get(url)
            .then((res) => {
              console.log("🚀 ~ handleSave ~ res:", res);
              const currLoc =
                res?.data?.arrestReports?.[0]?.currentLocation || "";
              const name = `${bookings?.descriptionCached || ""}`;
              setShowDetailsPopup(false);
              let showMovmentRules = false;

              if (
                bookings?.inmateMovementRule?.findIndex((rule) =>
                  dayjs().isSameOrBefore(dayjs(rule?.expirationDate), "day")
                ) !== -1
              ) {
                showMovmentRules = true;
              }

              if (showMovmentRules) {
                showDialog("info", {
                  title: `Movement Rules for ${removeNullFromText(name)}`,
                  content: getContentForMovementRules(currLoc),
                  confirmButtonText: "Ok",
                });
              }
            })
            .catch(() => {
              setShowDetailsPopup(false);
            })
            .finally(() => {
              setIsLoading(() => false);
            });
        })
        .catch((err) => {
          console.log(err);
        });
    };

    const getContentForMovementRules = (currLoc = "") => {
      let returnString = "";
      bookings?.inmateMovementRule?.forEach((rule) => {
        if (dayjs().isSameOrBefore(dayjs(rule?.expirationDate), "day")) {
          if (
            rule?.inmateMovementRuleTypeID ===
            REF_LIST_CONSTANT.Inmate_Movement_Rule_Type
              .INMATEMOVEMENTTYPE_COMMONRULES
          ) {
            returnString += `<p>${getRefListNameByID(
              "Inmate_Movement_Common",
              rule?.inmateMovementCommonID
            )}. ${rule?.comments || ""} </p>`;
          } else if (
            rule?.inmateMovementRuleTypeID ===
            REF_LIST_CONSTANT.Inmate_Movement_Rule_Type
              .INMATEMOVEMENTTYPE_KEEPSEPARATE
          ) {
            if (
              rule?.keepSeparateTypeID ===
              REF_LIST_CONSTANT.Keep_Separate_Type
                .KEEPSEPARATETYPE_SPECIFICPERSON
            ) {
              returnString += `<p>Keep Separate from ${
                rule?.jackets?.[0]?.bio?.[0]?.lName || ""
              } ${rule?.jackets?.[0]?.bio?.[0]?.fName || ""}. ${
                rule?.comments || ""
              } Current Location=  ${currLoc}</p>`;
            } else {
              returnString += `<p>Keep Separate from ${getRefListNameByID(
                "Keep_Separate_Type",
                rule?.keepSeparateTypeID
              )}. ${rule?.comments || ""} </p>`;
            }
          } else {
            returnString += `<p>${rule?.comments || ""} </p>`;
          }
        }
      });

      return <div dangerouslySetInnerHTML={{ __html: returnString }} />;
    };

    useEffect(() => {
      let tracking = _.clone(bookings?.inmateTracking);
      if (isNonEmptyVariable(tracking)) {
        tracking.reverse();
        let loan = null;
        let cell = null;
        tracking.forEach((e, i) => {
          if (
            loan == null &&
            e.inmateLocation == "Loan" &&
            e.movementStatus == "Complete"
          ) {
            loan = e;
          }
          if (
            cell == null &&
            e.inmateLocation == "In Jail" &&
            e.movementStatus == "Complete"
          ) {
            cell = e;
          }
        });
        if (isNonEmptyVariable(loan)) {
          setLastLoan(loan);
        }
        if (isNonEmptyVariable(cell)) {
          setLastCell(cell);
        }
      }
    }, [bookings]);
    useEffect(() => {
      let isLoan = bookings?.arrestReports?.[0]?.currentLocation;
      if (isNonEmptyVariable(isLoan)) {
        if (isLoan.includes("Loan")) {
          setIsInLoan(true);
        } else {
          setIsInLoan(false);
        }
      }
    }, [bookings]);

    useEffect(() => {
      if (isNonEmptyVariable(selectedRow) && !isReturn) {
        setTimeout(() => {
          // setShowDetailsPopup(true);
        }, 100);
      }
    }, [selectedRow, isReturn]);

    return (
      <>
        <HouseAndTracksTable
          details={bookings?.inmateTracking}
          handleAddClick={() => handleShowPopup(actionTypes.ADD)}
          handleEditClick={() => handleShowPopup(actionTypes.EDIT)}
          handleLogClick={handleLogClick}
          handleClearClick={handleClearClick}
          handleOnRowClick={handleOnRowClick}
          isEnabled={bookingsFieldsEnabled}
          isRowSelected={isNonEmptyVariable(selectedIndex)}
          setScheduled={setScheduled}
          setIsReturn={setIsReturn}
          isInLoan={isInLoan}
        />
        <HouseAndTracksForm
          showDetailsPopup={showDetailsPopup}
          setShowDetailsPopup={setShowDetailsPopup}
          handleSave={handleSave}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
          selectedRow={selectedRow}
          mode={mode}
          houseFormRef={houseFormRef}
          isEdit={isEdit}
          isLoading={isLoading}
          setIsEdit={setIsEdit}
          isScheduled={isScheduled}
          isReturn={isReturn}
          setScheduled={setScheduled}
          setIsReturn={setIsReturn}
          lastCell={lastCell}
          lastLoan={lastLoan}
          setLastCell={setLastCell}
          setLastLoan={setLastLoan}
          setSelectedRow={setSelectedRow}
          isInLoan={isInLoan}
          handleAddClick={() => handleShowPopup(actionTypes.ADD)}
          from="list"
        />

        <HouseAndTracksInfoTables
          showLogInfo={showLogInfo}
          setShowLogInfo={setShowLogInfo}
          data={bookings?.inmateTracking}
        />
        <HouseandTracksClearPopup
          show={showClearPopup}
          setShow={setShowClearPopup}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
          handleOk={handleClearingHousing}
        />
      </>
    );
  }
);

export default HouseTrack;
